import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './AgeEstimationScan.css';
import {
  PersonOffOutlined,
  VisibilityOutlined,
  WbSunnyOutlined
} from '@mui/icons-material';
import { useIdentification } from './IdentificationContext';
import config from './config';
import CameraCapture from './components/CameraCapture';
import {
  BackButton,
  EnterpriseCard,
  EnterpriseHero,
  EnterpriseShell,
  EnterpriseTopBar
} from './ui/EnterpriseLayout';

function AgeEstimationScan() {
  const navigate = useNavigate();
  const { identificationId } = useIdentification();
  const videoRef = useRef(null);
  const [instruction, setInstruction] = useState('Position your face');
  const [isCapturing, setIsCapturing] = useState(false);
  
  const guideMode = (() => {
    const t = (instruction || '').toLowerCase();
    // Video is now mirrored, so guide directions match the instruction directly
    if (t.includes('look left')) return 'left';
    if (t.includes('look right')) return 'right';
    return 'none';
  })();

  const handleCapture = async (blob) => {
    setIsCapturing(true);
    setInstruction('Capturing...');
    
    try {
      if (!identificationId) {
        throw new Error('No identification ID available');
      }

      const formData = new FormData();
      formData.append('file', blob, 'face.jpg');
      formData.append('documentType', 'ESTIMATE');

      const response = await fetch(`${config.API_BASE_URL}/identifications/${identificationId}/files`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload face image for age estimation');
      }

      const fileData = await response.json();
      console.log('Face image uploaded successfully for age estimation:', fileData);

      // Navigate to result screen with age estimation data
      setTimeout(() => {
        navigate('/age-verified', { 
          state: { 
            ageEstimationResult: fileData,
            isAgeEstimation: true
          } 
        });
      }, 500);
    } catch (error) {
      console.error('Error capturing face:', error);
      setIsCapturing(false);
      setInstruction('Error capturing. Please try again.');
      alert('Failed to capture face. Please try again.');
    }
  };

  return (
    <EnterpriseShell>
      <EnterpriseTopBar
        right={
          <>
            <BackButton onClick={() => navigate(-1)} />
            <span className="vt-chip vt-chip--subtle">Age estimation</span>
          </>
        }
      />

      <EnterpriseHero
        meta="Live camera scan"
        title="Position your face"
      />

      <div className="age-estimation-scan-enterprise">
        <EnterpriseCard>
          <div className="scan-camera-container">
            <div className={`liveness-guide ${guideMode !== 'none' ? 'show' : ''}`}>
              <div className={`liveness-guide-side left ${guideMode === 'left' ? 'active' : ''}`} />
              <div className={`liveness-guide-side right ${guideMode === 'right' ? 'active' : ''}`} />
              <div className={`liveness-guide-ring ${guideMode === 'blink' ? 'active' : ''}`} />
            </div>
            <div className="scan-camera-wrapper">
              <CameraCapture
                mode="face"
                facingMode="user"
                onCapture={handleCapture}
                showCameraSelector={true}
                instruction={instruction}
                onInstructionChange={setInstruction}
                autoCapture={true}
                videoRef={videoRef}
              />
            </div>
            <img src="/Face-No-Background.png" alt="Face outline" className="face-outline-image" />
            {instruction && <div className="instruction-bubble">{instruction}</div>}
            {isCapturing && (
              <div className="capturing-overlay">
                <div className="capturing-spinner"></div>
              </div>
            )}
          </div>

          <ul className="vt-list" style={{ marginTop: '18px' }}>
            <li className="vt-listItem">
              <WbSunnyOutlined className="vt-listIcon" />
              <span>Find a well-lit area</span>
            </li>
            <li className="vt-listItem">
              <PersonOffOutlined className="vt-listIcon" />
              <span>Remove any headwear</span>
            </li>
            <li className="vt-listItem">
              <VisibilityOutlined className="vt-listIcon" />
              <span>Keep your glasses on</span>
            </li>
          </ul>
        </EnterpriseCard>
      </div>
    </EnterpriseShell>
  );
}

export default AgeEstimationScan;
